"use client";

import { safeT } from "@/utils/safeT";
import { PushPin, PushPinOutlined } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";

type PinToggleButtonProps = {
  id: string;
  lang: string;
  initialPin?: boolean;
  fontSize?: number;
  onToggle?: (id: string, lang: string, pin: boolean) => void;
};

export default function PinToggleButton({
  id,
  lang,
  initialPin = true,
  fontSize = 20,
  onToggle,
}: PinToggleButtonProps) {
  const [pin, setPin] = useState(initialPin);
  const [lastClicked, setLastClicked] = useState(0);
  const t = useTranslations();
  const handleToggle = useCallback(() => {
    const now = Date.now();
    if (now - lastClicked < 1500) return;
    setLastClicked(now);

    const newPin = !pin;
    setPin(newPin);
    if (onToggle) onToggle(id, lang, newPin);
  }, [pin, lastClicked, id, lang, onToggle]);

  const tooltipText = pin
    ? safeT(t, "ui.label.unpin", {
        defaultMessage: "고정 해제",
      })
    : safeT(t, "ui.label.pin", {
        defaultMessage: "고정",
      });

  return (
    <Tooltip title={tooltipText} arrow>
      <IconButton
        aria-label={tooltipText}
        size="small"
        onClick={handleToggle}
        sx={{
          borderRadius: "8px",
          px: 0.8,
          py: 0.5,
        }}
      >
        {pin ? (
          <PushPin
            sx={{
              fontSize,
              color: "text.secondary",
              transition: "transform 0.3s ease, color 0.3s ease",
            }}
          />
        ) : (
          <PushPinOutlined
            sx={{
              fontSize,
              color: "text.secondary",
              transform: "rotate(45deg)",
              transition: "transform 0.3s ease, color 0.3s ease",
            }}
          />
        )}
      </IconButton>
    </Tooltip>
  );
}
